// TODO: either don't import or move to @pathwright/ux
import { useToggleIntercomUI } from "@pathwright/features/src/modules/intercom/IntercomProvider"

// NOTE: we use legacy ui ErrorBoundary because it works whereas the ux ErrorBoundary does not.
// We need to fully replace legacy ui ErrorBoundary with ux ErrorBoundary.
import ErrorBoundary from "@pathwright/ui/src/components/error/ErrorBoundary"

import { Drawer, Portal } from "@chakra-ui/react"
// import { useScrollApp } from "@pathwright/utils/src/scroll/useScroll"
// import { useMotionValueEvent } from "framer-motion"
import React from "react"
import {
  AugmentationDialogProvider,
  useAugmentationDialogs
} from "./AugmentationDialog"
import { AugmentationPanel } from "./AugmentationLayout"
import {
  AugmentationPanelFooterProvider,
  useAugmentationPanelFooterHostRef
} from "./AugmentationPanelFooter"
import AugmentationPanelNav from "./AugmentationPanelNav"
import { useToolbarPosition } from "./AugmentationPositionedToolbar"
import AugmentationToolbarRouting from "./AugmentationToolbarRouting"
import { AUGMENTATION_PANEL_SCROLL_CSS } from "./augmentationPanelStyles"
import useAugmentationPanel, { resolvePanelWidth } from "./useAugmentationPanel"
import useAugmentationPanelEscape from "./useAugmentationPanelEscape"
import useScrollOverflow from "./useScrollOverflow"

function AugmentationRoutedPanelFooter() {
  const footerRef = useAugmentationPanelFooterHostRef()

  return (
    // Always mounted so destinations can portal actions here.
    // _empty hides it on list destinations with no actions.
    <Drawer.Footer
      ref={footerRef}
      p={0}
      w="full"
      flexDirection="column"
      alignItems="stretch"
      _empty={{ display: "none" }}
    />
  )
}

const AugmentationRoutedPanel: AugmentationPanel = React.forwardRef(
  (
    {
      toolbar,
      disclosure,
      gutter,
      offset,
      allowOverlay,
      size,
      drawerContentProps,
      usePortal = false,
      trapFocus = false,
      queryParamKey = "panel"
    },
    ref
  ) => {
    // Calculating the top offset based on the navbar height and the window
    // scroll y value. We basically don't want to offset the panel if the navbar
    // is out of view.
    // const [scrollY, setScrollY] = React.useState(0)
    // const scroll = useScrollApp()
    // useMotionValueEvent(scroll.scrollY, "change", () => {
    //   const nextScrollY = scroll.scrollY.get()
    //   if (Math.min(scrollY, nextScrollY) < navbarHeight) setScrollY(nextScrollY)
    // })

    // const { getRectValue } = useObserveSizeContext()
    // const navbarHeight = getRectValue("navbar.height")
    // const alertHeight = getRectValue("alert.height")
    // const topOffset = Math.max(navbarHeight - scrollY, 0) + alertHeight

    // For now, ignore top nav bar and alert banner offsets.
    const topOffset = 0

    // Hardcoding the bottom offset based on the (expected) toolbar top.
    // We could also use the ObserveSizeContext but there's currently a
    // bug where subsequent renders add to the rect size over time (should
    // remain the same)
    const bottomOffset = 60

    // Offsetting the panel based on the toolbar position.
    const { bottom, top } = useToolbarPosition(
      {
        bottom: { bottom: bottomOffset, top: 0 },
        side: { bottom: 0, top: topOffset }
      },
      allowOverlay
    )

    // Hide Intercom UI when drawer is open.
    useToggleIntercomUI(disclosure?.open)
    const dialogs = useAugmentationDialogs()
    const bodyScroll = useScrollOverflow<HTMLDivElement>()

    const { getDrawerProps, getDrawerPositionerProps, getDrawerContentProps } =
      useAugmentationPanel({
        disclosure,
        // Layout can override these via offset.top / offset.bottom / offset.right.
        offset: { top, bottom, ...offset },
        allowOverlay,
        gutter,
        size,
        width: resolvePanelWidth(
          toolbar.stack.map((option) => option.panelWidth)
        )
      })

    const handleEscape = useAugmentationPanelEscape({
      toolbar,
      disclosure,
      queryParamKey,
      hasOpenDialog: dialogs.hasOpenDialog
    })

    return (
      <AugmentationDialogProvider dialogs={dialogs}>
        <AugmentationPanelFooterProvider>
          <AugmentationToolbarRouting
            toolbar={toolbar}
            queryParamKey={queryParamKey}
          >
            {(routes) => {
              const positioner = (
                <Drawer.Positioner {...getDrawerPositionerProps()}>
                  <Drawer.Content
                    ref={ref as React.Ref<HTMLDivElement>}
                    {...getDrawerContentProps()}
                    {...drawerContentProps}
                  >
                    <Drawer.Header p={0}>
                      <AugmentationPanelNav
                        toolbar={toolbar}
                        disclosure={disclosure}
                      />
                    </Drawer.Header>
                    <Drawer.Body
                      ref={bodyScroll.ref}
                      p={0}
                      pt="15px"
                      minH={0}
                      css={AUGMENTATION_PANEL_SCROLL_CSS}
                      {...bodyScroll.props}
                    >
                      <ErrorBoundary>{routes}</ErrorBoundary>
                    </Drawer.Body>
                    <AugmentationRoutedPanelFooter />
                  </Drawer.Content>
                </Drawer.Positioner>
              )

              return (
                <Drawer.Root
                  {...getDrawerProps()}
                  trapFocus={trapFocus}
                  onEscapeKeyDown={handleEscape}
                >
                  {usePortal ? <Portal>{positioner}</Portal> : positioner}
                </Drawer.Root>
              )
            }}
          </AugmentationToolbarRouting>
        </AugmentationPanelFooterProvider>
      </AugmentationDialogProvider>
    )
  }
)

export default AugmentationRoutedPanel
